// 1:1 booth room on top of Trystero (serverless signaling over Nostr relays,
// media + data go peer-to-peer over WebRTC; TURN from config.ts as fallback).

import { joinRoom, selfId, type MessageAction, type MessageContext, type Room as TRoom } from 'trystero';
import { APP_ID, iceServers } from '../config';
import { ClockSync, sampleFrom } from './clock';
import {
  CAPS, PROTOCOL_VERSION,
  type BoothConfig, type BurstPick, type DoneMsg, type Hello, type LayoutState, type PoseFrameMsg, type PoseMsg, type PreviewMeta, type Role,
  type SceneRequest, type SessionInfo, type ShotAck, type ShotCmd, type TimePing, type TimePong,
} from './protocol';
import { PoseRing, decodePose, encodePose, type PoseFrame } from '../booth/pose';
import { registry } from '../media/registry';
import { readStats, tuneForLatency, type LinkStats } from './tuning';

type Listener<T> = (v: T) => void;

class Emitter<E extends Record<string, unknown>> {
  private m = new Map<keyof E, Set<Listener<any>>>();
  on<K extends keyof E>(k: K, fn: Listener<E[K]>) {
    if (!this.m.has(k)) this.m.set(k, new Set());
    this.m.get(k)!.add(fn);
    return () => this.m.get(k)!.delete(fn);
  }
  emit<K extends keyof E>(k: K, v: E[K]) {
    this.m.get(k)?.forEach((fn) => fn(v));
  }
}

export interface RoomEvents extends Record<string, unknown> {
  partner: { id: string; name: string; role: Role } | null;
  stream: MediaStream;
  pose: PoseMsg;
  /** v0.5: per-frame pose of the partner (already decoded; also pushed into `remotePoses`) */
  poseFrame: PoseFrame;
  /** v0.5: host-authoritative layout (only newer versions are emitted) */
  layout: LayoutState;
  config: BoothConfig;
  shot: ShotCmd;
  shotAck: ShotAck;
  session: SessionInfo;
  preview: { blob: Blob; meta: PreviewMeta };
  done: DoneMsg;
  sceneRequest: SceneRequest;
  /** v0.6: host stopped the shoot of this session */
  stopShoot: { session: string };
  burstPick: BurstPick;
  customBg: Blob;
  progress: { what: string; pct: number };
  full: void;
  stats: LinkStats & { clockErr: number; clockOffset: number };
  status: string;
}

const now = () => performance.now();

type ActionName =
  | 'hello' | 'accept' | 'full' | 'cfg' | 'pose' | 'pf' | 'layout' | 'tping' | 'tpong'
  | 'shot' | 'shotack' | 'sess' | 'preview' | 'done' | 'custombg' | 'scenereq' | 'stop' | 'pick';

export class BoothRoom extends Emitter<RoomEvents> {
  readonly selfId = selfId;
  readonly clock = new ClockSync();
  partnerId: string | null = null;
  lastStats: (LinkStats & { clockErr: number; clockOffset: number }) | null = null;
  /** v0.5: partner's per-frame poses keyed by frame seq (render/stage.ts joins them by the barcode) */
  readonly remotePoses = new PoseRing();
  /** capabilities the partner announced (empty for v2 peers) */
  partnerCaps: string[] = [];
  /** latest layout version seen (sent or received) */
  layoutVersion = -1;

  private room: TRoom;
  private timers: ReturnType<typeof setInterval>[] = [];
  private unsubs: (() => void)[] = [];
  private act: Record<ActionName, MessageAction<any>>;

  constructor(
    readonly code: string,
    readonly role: Role,
    readonly name: string,
    private readonly localStream: MediaStream,
    relays: string[] = [],
  ) {
    super();
    this.room = joinRoom(
      {
        appId: APP_ID,
        password: `booth-${code}`, // encrypts SDP on the relays
        rtcConfig: { iceServers: iceServers(), bundlePolicy: 'max-bundle' },
        ...(relays.length ? { relayConfig: { urls: relays } } : {}),
      },
      code,
      { onJoinError: (e) => this.emit('status', `Lỗi vào phòng: ${e.error}`) },
    );
    this.act = this.makeActions();

    this.room.onPeerJoin = (id) => {
      this.act.hello.send({ role, name, v: PROTOCOL_VERSION, caps: CAPS } satisfies Hello, { target: id });
    };
    this.room.onPeerLeave = (id) => {
      if (id === this.partnerId) {
        this.partnerId = null;
        this.remotePoses.clear();
        this.emit('partner', null);
        this.emit('status', 'Bạn kia đã rời phòng');
      }
    };
    this.room.onPeerStream = (stream, id) => {
      if (id !== this.partnerId) return;
      this.emit('stream', stream);
      this.tune();
    };

    // v0.5: the pipeline whose packed track we send streams its per-frame poses to the partner
    registry.room = this;
    const sent = new Set(localStream.getVideoTracks().map((t) => t.id));
    for (const p of registry.pipelines) {
      if (p.stream.getVideoTracks().some((t) => sent.has(t.id))) this.unsubs.push(p.addPoseListener((f) => this.sendPoseFrame(f)));
    }

    this.timers.push(setInterval(() => this.pollStats(), 1000));
    if (role === 'guest') this.timers.push(setInterval(() => this.syncClock(), 1500));
  }

  private makeActions(): Record<ActionName, MessageAction<any>> {
    const mk = (
      name: ActionName,
      fn: (data: any, ctx: MessageContext) => void,
      progress?: (pct: number) => void,
    ) => this.room.makeAction<any>(name, {
      onMessage: (d, c) => { fn(d, c); },
      ...(progress ? { onReceiveProgress: (p: number) => progress(p) } : {}),
    });
    const fromPartner = (fn: (d: any, c: MessageContext) => void) =>
      (d: any, c: MessageContext) => { if (c.peerId === this.partnerId) fn(d, c); };

    return {
      hello: mk('hello', (d, c) => this.onHello(d as Hello, c.peerId)),
      accept: mk('accept', (_d, c) => this.onAccepted(c.peerId)),
      full: mk('full', () => this.emit('full', undefined)),
      cfg: mk('cfg', fromPartner((d) => this.emit('config', d as BoothConfig))),
      pose: mk('pose', fromPartner((d) => this.emit('pose', d as PoseMsg))),
      pf: mk('pf', fromPartner((d) => {
        const f = decodePose(d as PoseFrameMsg);
        this.remotePoses.push(f);
        this.emit('poseFrame', f);
      })),
      layout: mk('layout', fromPartner((d) => {
        const l = d as LayoutState;
        if (typeof l?.version !== 'number' || l.version <= this.layoutVersion) return;
        this.layoutVersion = l.version;
        this.emit('layout', l);
      })),
      tping: mk('tping', (d, c) => {
        const t1 = now();
        if (this.role !== 'host') return;
        void this.act.tpong.send({ t0: (d as TimePing).t0, t1, t2: now() } satisfies TimePong, { target: c.peerId });
      }),
      tpong: mk('tpong', (d) => {
        const p = d as TimePong;
        this.clock.add(sampleFrom(p.t0, p.t1, p.t2, now()));
      }),
      shot: mk('shot', fromPartner((d) => this.emit('shot', d as ShotCmd))),
      shotack: mk('shotack', fromPartner((d) => this.emit('shotAck', d as ShotAck))),
      sess: mk('sess', fromPartner((d) => this.emit('session', d as SessionInfo))),
      preview: mk('preview',
        fromPartner((d, c) => this.emit('preview', { blob: toBlob(d, 'image/webp'), meta: c.metadata as unknown as PreviewMeta })),
        (pct) => this.emit('progress', { what: 'Nhận ảnh xem trước', pct })),
      done: mk('done', fromPartner((d) => this.emit('done', d as DoneMsg))),
      scenereq: mk('scenereq', fromPartner((d) => this.emit('sceneRequest', d as SceneRequest))),
      pick: mk('pick', fromPartner((d) => this.emit('burstPick', d as BurstPick))),
      stop: mk('stop', fromPartner((d) => {
        if (typeof d?.session === 'string') this.emit('stopShoot', { session: d.session });
      })),
      custombg: mk('custombg', (d) => this.emit('customBg', toBlob(d, 'image/jpeg'))),
    };
  }

  private onHello(h: Hello, peerId: string): void {
    if (this.role === 'host') {
      if (h.role === 'host') return; // two hosts with the same code: ignore
      if (this.partnerId && this.partnerId !== peerId) {
        this.act.full.send(null, { target: peerId });
        return;
      }
      this.setPartner(peerId, h);
      this.act.accept.send(null, { target: peerId });
    } else if (h.role === 'host') {
      // guests only talk to the host; wait for its "accept"
      this.pendingHost = { id: peerId, hello: h };
    }
  }

  private pendingHost: { id: string; hello: Hello } | null = null;

  private onAccepted(peerId: string) {
    if (this.role !== 'guest') return;
    const h = this.pendingHost?.id === peerId ? this.pendingHost.hello : { role: 'host' as Role, name: 'Chủ phòng', v: 1 };
    this.setPartner(peerId, h);
    for (let i = 0; i < 6; i++) setTimeout(() => this.syncClock(), i * 120);
  }

  private setPartner(id: string, h: Hello) {
    this.partnerId = id;
    this.partnerCaps = Array.isArray(h.caps) ? h.caps : [];
    this.remotePoses.clear();
    this.tuneTries = 0;
    this.emit('partner', { id, name: h.name, role: h.role });
    Promise.all(this.room.addStream(this.localStream, { target: id })).then(() => this.tune());
  }

  private tuneTries = 0;

  /** Apply low-latency settings; retried until negotiation has produced encodings. */
  private tune() {
    const pc = this.partnerId ? this.room.getPeers()[this.partnerId] : null;
    if (!pc) return;
    void tuneForLatency(pc).then((ok) => {
      if (!ok && this.tuneTries++ < 20) setTimeout(() => this.tune(), 500);
    });
  }

  private syncClock() {
    if (this.partnerId) this.act.tping.send({ t0: now() } satisfies TimePing, { target: this.partnerId });
  }

  /** Several quick pings in a row; resolves once the answers had time to arrive. */
  async burstSync(n: number, gapMs: number) {
    for (let i = 0; i < n; i++) setTimeout(() => this.syncClock(), i * gapMs);
    await new Promise((r) => setTimeout(r, n * gapMs + 150));
  }

  private async pollStats() {
    const pc = this.partnerId ? this.room.getPeers()[this.partnerId] : null;
    if (!pc) return;
    const s = await readStats(pc).catch(() => null);
    if (!s) return;
    this.lastStats = {
      ...s,
      clockErr: this.role === 'host' ? 0 : this.clock.errorBound,
      clockOffset: this.role === 'host' ? 0 : this.clock.offset,
    };
    this.emit('stats', this.lastStats);
  }

  /** Current time on the host's clock (host: own clock). */
  hostNow() {
    return this.role === 'host' ? now() : this.clock.localToHost(now());
  }

  hostToLocal(t: number) {
    return this.role === 'host' ? t : this.clock.hostToLocal(t);
  }

  private to() {
    return this.partnerId ? { target: this.partnerId } : null;
  }

  sendConfig(c: BoothConfig) { const t = this.to(); if (t) void this.act.cfg.send(c as any, t); }
  sendPose(p: PoseMsg) { const t = this.to(); if (t) void this.act.pose.send(p as any, t); }
  /** v0.5: per-frame pose (call from PersonPipeline.onPose); skipped for v2 partners */
  sendPoseFrame(p: PoseFrame) {
    const t = this.to();
    if (t && this.partnerCaps.includes('pf')) void this.act.pf.send(encodePose(p) as any, t);
  }
  /** v0.5 host: broadcast the layout (versions must increase) */
  sendLayout(l: LayoutState) {
    this.layoutVersion = Math.max(this.layoutVersion, l.version);
    const t = this.to();
    if (t) void this.act.layout.send(l as any, t);
  }
  sendShot(s: ShotCmd) { const t = this.to(); if (t) void this.act.shot.send(s as any, t); }
  sendShotAck(a: ShotAck) { const t = this.to(); if (t) void this.act.shotack.send(a as any, t); }
  sendSession(info: SessionInfo) { const t = this.to(); if (t) void this.act.sess.send(info as any, t); }
  async sendPreview(blob: Blob, meta: PreviewMeta) {
    const t = this.to();
    if (t) await this.act.preview.send(blob, { ...t, metadata: meta as any });
  }
  sendSceneRequest(r: SceneRequest) { const t = this.to(); if (t) void this.act.scenereq.send(r as any, t); }
  sendPick(p: BurstPick) { const t = this.to(); if (t) void this.act.pick.send(p as any, t); }
  sendStop(session: string) { const t = this.to(); if (t) void this.act.stop.send({ session } as any, t); }
  sendDone(d: DoneMsg) { const t = this.to(); if (t) void this.act.done.send(d as any, t); }
  async sendCustomBg(blob: Blob) { const t = this.to(); if (t) await this.act.custombg.send(blob, t); }

  async leave() {
    this.timers.forEach(clearInterval);
    this.unsubs.forEach((f) => f());
    this.unsubs = [];
    if (registry.room === this) registry.room = null;
    await this.room.leave();
  }
}

function toBlob(d: unknown, type: string): Blob {
  if (d instanceof Blob) return d;
  if (d instanceof ArrayBuffer || ArrayBuffer.isView(d)) return new Blob([d as BlobPart], { type });
  throw new Error('unexpected payload');
}

const ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';

/** 6-char room code without ambiguous characters (0/O, 1/I/L). */
export function makeRoomCode(rand: () => number = Math.random) {
  let s = '';
  for (let i = 0; i < 6; i++) s += ALPHABET[Math.floor(rand() * ALPHABET.length)];
  return s;
}

export function normalizeRoomCode(s: string) {
  return s.toUpperCase().split('').filter((c) => ALPHABET.includes(c)).join('').slice(0, 6);
}
